/* LeaveMemory.jsx — public visitor flow for sharing a written memory.
   Reachable from the "Leave a memory" button on every public memorial.
   Submits to POST /api/public/memorials/:slug/tributes; admin reviews
   in the Access Requests tab before publishing. */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Logo } from '../components/Logo';
import { Navbar, Footer } from '../components/Navbar';
import { Heart, Check, ChevronLeft, Video, X } from 'lucide-react';
import { AIWritingAssist } from '../components/AIWritingAssist';
import { toast } from 'sonner';

const NAME_MAX = 80;
const RELATIONSHIP_MAX = 80;
const MESSAGE_MIN = 8;
const MESSAGE_MAX = 1500;
const VIDEO_MAX_MB = 50;

export default function LeaveMemory() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [memorial, setMemorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [message, setMessage] = useState('');
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/memorials/${slug}`)
      .then(({ data }) => setMemorial(data.memorial))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Logo size={40} />
            <div className="mt-6 font-serif text-xl text-[hsl(var(--primary))]">
              Loading…
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (err || !memorial) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-center">
          <div>
            <h1 className="font-serif text-3xl text-[hsl(var(--primary))]">
              We couldn't find that memorial
            </h1>
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
              {err || 'It may have been moved or unpublished.'}
            </p>
            <Link to="/" className="ml-btn-outline mt-6 inline-flex">Return home</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const onPickVideo = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) {
      setErr('Please choose a video file (MP4, MOV, or WebM).');
      return;
    }
    if (f.size > VIDEO_MAX_MB * 1024 * 1024) {
      setErr(`That video is larger than ${VIDEO_MAX_MB} MB. Please trim and try again.`);
      return;
    }
    setErr(null);
    setVideo(f);
    setVideoPreview(URL.createObjectURL(f));
  };

  const clearVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideo(null);
    setVideoPreview(null);
    setUploadProgress(0);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (name.trim().length < 1) return setErr('Please add your name.');
    if (message.trim().length < MESSAGE_MIN) {
      return setErr(`Please share a memory of at least ${MESSAGE_MIN} characters.`);
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name.trim().slice(0, NAME_MAX));
      fd.append('message', message.trim().slice(0, MESSAGE_MAX));
      if (relationship.trim()) {
        fd.append('relationship', relationship.trim().slice(0, RELATIONSHIP_MAX));
      }
      if (video) fd.append('video', video);
      await api.post(`/public/memorials/${slug}/tributes`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setSubmitted(true);
      toast.success(
        video
          ? 'Thank you — your memory and video have been received.'
          : 'Thank you — your memory has been received.',
      );
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-lg text-center" data-testid="leave-memory-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
              <Check className="w-7 h-7 text-[hsl(var(--accent))]" strokeWidth={1.4} />
            </div>
            <h1 className="font-serif text-3xl mt-7 text-[hsl(var(--primary))]">
              Thank you for sharing.
            </h1>
            <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed font-light">
              Your memory of <span className="italic">{memorial.full_name}</span> has been
              sent to the family. They will review it before it appears on the
              public memorial.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/m/${slug}`}
                className="ml-btn-outline"
                data-testid="leave-memory-back-to-memorial"
              >
                Return to memorial
              </Link>
              <button
                type="button"
                className="ml-btn-link"
                data-testid="leave-memory-add-another"
                onClick={() => {
                  setSubmitted(false);
                  setName('');
                  setRelationship('');
                  setMessage('');
                }}
              >
                Share another memory
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="leave-memory-page">
      <Navbar />
      <div className="flex-1">
        <div className="ml-container py-12 max-w-2xl">
          <button
            type="button"
            onClick={() => navigate(`/m/${slug}`)}
            className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] flex items-center gap-1.5 transition-colors"
            data-testid="leave-memory-back-link"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back to memorial
          </button>

          <div className="mt-10 text-center">
            <div className="inline-flex w-12 h-12 rounded-full bg-[hsl(var(--accent))]/10 items-center justify-center">
              <Heart className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.4} />
            </div>
            <div className="ml-overline mt-5">A memory for</div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl mt-2 text-[hsl(var(--primary))] tracking-tight">
              {memorial.full_name}
            </h1>
            <p className="mt-5 ml-caption leading-relaxed">
              Share a moment, a story, or a few quiet words.
              The family will review your memory before it appears here.
            </p>
          </div>

          <form onSubmit={submit} className="mt-12 space-y-6" data-testid="leave-memory-form">
            <div>
              <label className="ml-overline">Your name</label>
              <input
                type="text"
                value={name}
                maxLength={NAME_MAX}
                onChange={(e) => setName(e.target.value)}
                required
                className="ml-input mt-2"
                placeholder="e.g. Sarah Mitchell"
                data-testid="leave-memory-name"
              />
            </div>

            <div>
              <label className="ml-overline">Relationship <span className="lowercase tracking-normal text-[hsl(var(--primary)/0.45)]">(optional)</span></label>
              <input
                type="text"
                value={relationship}
                maxLength={RELATIONSHIP_MAX}
                onChange={(e) => setRelationship(e.target.value)}
                className="ml-input mt-2"
                placeholder="e.g. Neighbor for 25 years"
                data-testid="leave-memory-relationship"
              />
            </div>

            <div>
              <label className="ml-overline">Your memory</label>
              <textarea
                rows={7}
                value={message}
                maxLength={MESSAGE_MAX}
                onChange={(e) => setMessage(e.target.value)}
                required
                className="ml-input mt-2 leading-relaxed"
                placeholder="A moment, a story, a kindness — anything that helped you know them."
                data-testid="leave-memory-message"
              />
              <div className="mt-1.5 text-[11px] text-[hsl(var(--primary)/0.5)] flex justify-between">
                <span>Minimum {MESSAGE_MIN} characters</span>
                <span data-testid="leave-memory-char-count">
                  {message.length} / {MESSAGE_MAX}
                </span>
              </div>
              <AIWritingAssist
                context="memory"
                deceasedName={memorial?.full_name || ''}
                relationship={relationship}
                getCurrentText={() => message}
                onInsert={(text) => setMessage(text.slice(0, MESSAGE_MAX))}
                testIdPrefix="memory-ai-assist"
              />
            </div>

            <div>
              <label className="ml-overline">
                Add a video <span className="lowercase tracking-normal text-[hsl(var(--primary)/0.45)]">(optional · up to {VIDEO_MAX_MB} MB)</span>
              </label>
              {!video ? (
                <label
                  className="mt-2 flex items-center gap-3 justify-center rounded-md border border-dashed border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.02)] px-4 py-6 cursor-pointer hover:border-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/5 transition-colors"
                  data-testid="leave-memory-video-picker"
                >
                  <Video className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                  <span className="text-sm text-[hsl(var(--primary)/0.7)]">
                    Tap to record or upload a short video memory
                  </span>
                  <input
                    type="file"
                    accept="video/*"
                    capture="environment"
                    className="hidden"
                    onChange={onPickVideo}
                    data-testid="leave-memory-video-input"
                  />
                </label>
              ) : (
                <div className="mt-2 rounded-md border border-[hsl(var(--primary)/0.2)] bg-[hsl(var(--card))] p-3" data-testid="leave-memory-video-preview-card">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[hsl(var(--primary))] truncate">
                        {video.name}
                      </div>
                      <div className="text-[11px] text-[hsl(var(--primary)/0.55)]">
                        {(video.size / (1024 * 1024)).toFixed(1)} MB · {video.type || 'video'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearVideo}
                      className="p-1 rounded hover:bg-[hsl(var(--primary)/0.06)] text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))] transition-colors shrink-0"
                      data-testid="leave-memory-video-clear"
                      aria-label="Remove video"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  {videoPreview && (
                    <video
                      src={videoPreview}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full rounded bg-black"
                      style={{ maxHeight: 280 }}
                      data-testid="leave-memory-video-preview"
                    />
                  )}
                  {submitting && uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mt-2">
                      <div className="h-1 rounded-full bg-[hsl(var(--primary)/0.1)] overflow-hidden">
                        <div
                          className="h-full bg-[hsl(var(--accent))] transition-[width] duration-150"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <div className="text-[10px] text-[hsl(var(--primary)/0.55)] mt-1 text-right">
                        Uploading… {uploadProgress}%
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {err && (
              <div
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-sm p-3"
                data-testid="leave-memory-error"
              >
                {err}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="ml-btn-primary flex-1"
                data-testid="leave-memory-submit"
              >
                {submitting
                  ? (video ? `Uploading… ${uploadProgress || 0}%` : 'Sending…')
                  : 'Share this memory'}
              </button>
              <Link
                to={`/m/${slug}`}
                className="ml-btn-outline flex-1 text-center"
                data-testid="leave-memory-cancel"
              >
                Cancel
              </Link>
            </div>

            <p className="text-[11px] text-[hsl(var(--primary)/0.5)] leading-relaxed font-light text-center">
              Your memory will be reviewed by the family before it is published on
              this memorial. We never share your name or message elsewhere.
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
